import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

// Defined OUTSIDE ChangePasswordPage so React never remounts it on re-render
function PasswordInput({ field, label, placeholder, value, showPassword, error, onChangeValue, onToggleShow }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="relative">
        <input
          type={showPassword ? 'text' : 'password'}
          value={value}
          onChange={e => onChangeValue(field, e.target.value)}
          placeholder={placeholder}
          className={`input-field pr-10 ${error ? 'border-red-300 focus:ring-red-400' : ''}`}
        />
        <button
          type="button"
          onClick={() => onToggleShow(field)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

const STRENGTH_CHECKS = [
  { key: 'len',   test: p => p.length >= 8,          text: 'At least 8 characters' },
  { key: 'num',   test: p => /\d/.test(p),            text: 'Contains a number' },
  { key: 'upper', test: p => /[A-Z]/.test(p),         text: 'Contains uppercase (recommended)' },
  { key: 'spec',  test: p => /[^A-Za-z0-9]/.test(p), text: 'Contains special character (recommended)' },
];

export default function ChangePasswordPage({ forced = false }) {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [show, setShow] = useState({ current_password: false, new_password: false, confirm_password: false });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChangeValue = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    setErrors(e => ({ ...e, [field]: '' }));
  };

  const handleToggleShow = (field) => {
    setShow(s => ({ ...s, [field]: !s[field] }));
  };

  const validate = () => {
    const e = {};
    if (!forced && !form.current_password) e.current_password = 'Enter your current password';
    if (!form.new_password) e.new_password = 'Enter a new password';
    else if (form.new_password.length < 8) e.new_password = 'Must be at least 8 characters';
    else if (!/\d/.test(form.new_password)) e.new_password = 'Must contain at least one number';
    if (form.new_password !== form.confirm_password) e.confirm_password = 'Passwords do not match';
    return e;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true); setErrors({});
    try {
      await api.post('/auth/change-password', {
        current_password: form.current_password || undefined,
        new_password: form.new_password,
      });
      toast.success('Password changed successfully');
      await refreshUser();
      navigate('/');
    } catch (err) {
      setErrors({ submit: err?.response?.data?.error || 'Failed to change password' });
    } finally { setSaving(false); }
  };

  // useMemo so strength checks don't recompute on unrelated state changes
  const strengthChecks = useMemo(
    () => STRENGTH_CHECKS.map(c => ({ ...c, ok: c.test(form.new_password) })),
    [form.new_password]
  );

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1e3a5f] rounded-2xl mb-4 shadow-lg">
            <KeyRound size={28} className="text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            {forced ? 'Set a New Password' : 'Change Password'}
          </h1>
          {forced && (
            <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mt-3">
              For security, please set a new password before continuing. Your current password is a default and must be changed.
            </p>
          )}
          {!forced && <p className="text-gray-500 text-sm mt-1">Update your account password</p>}
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          {user && (
            <div className="flex items-center gap-3 mb-6 p-3 bg-gray-50 rounded-xl">
              <div className="w-9 h-9 bg-[#f0a500] rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                {user.name?.[0]}
              </div>
              <div>
                <div className="text-sm font-semibold text-gray-800">{user.name}</div>
                <div className="text-xs text-gray-500">{user.email}</div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!forced && (
              <PasswordInput
                field="current_password"
                label="Current Password"
                placeholder="Your current password"
                value={form.current_password}
                showPassword={show.current_password}
                error={errors.current_password}
                onChangeValue={handleChangeValue}
                onToggleShow={handleToggleShow}
              />
            )}
            <PasswordInput
              field="new_password"
              label="New Password"
              placeholder="Min 8 chars, at least one number"
              value={form.new_password}
              showPassword={show.new_password}
              error={errors.new_password}
              onChangeValue={handleChangeValue}
              onToggleShow={handleToggleShow}
            />
            <PasswordInput
              field="confirm_password"
              label="Confirm New Password"
              placeholder="Repeat the new password"
              value={form.confirm_password}
              showPassword={show.confirm_password}
              error={errors.confirm_password}
              onChangeValue={handleChangeValue}
              onToggleShow={handleToggleShow}
            />

            {form.new_password && (
              <div className="space-y-1">
                {strengthChecks.map(({ key, ok, text }) => (
                  <div key={key} className={`flex items-center gap-1.5 text-xs ${ok ? 'text-green-600' : 'text-gray-400'}`}>
                    <ShieldCheck size={12} className={ok ? 'text-green-500' : 'text-gray-300'} />
                    {text}
                  </div>
                ))}
              </div>
            )}

            {errors.submit && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{errors.submit}</p>
            )}

            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={saving}
                className="flex-1 btn-primary py-2.5 flex items-center justify-center gap-2">
                {saving ? 'Saving…' : 'Change Password'}
              </button>
              {!forced && (
                <button type="button" onClick={() => navigate(-1)} className="flex-1 btn-secondary py-2.5">
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
